import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  CircularProgress,
} from '@mui/material';
import { createSchoolDirect, updateSchoolDirect, sendInviteDirect } from '@timetable/api';
import { queryKeys } from '../../lib/queryKeys';
import type { CreateSchoolRequest, School, UpdateSchoolRequest } from '@timetable/types';

interface SchoolRegistrationDialogProps {
  open: boolean;
  onClose: () => void;
  school?: School | null; // if provided, dialog is in edit mode
}

export function SchoolRegistrationDialog({ open, onClose, school }: SchoolRegistrationDialogProps) {
  const queryClient = useQueryClient();
  const isEditMode = !!school;

  // Form state
  const [formData, setFormData] = useState<CreateSchoolRequest>({
    school_name: '',
    address: '',
    contact_person: '',
    email: '',
    phone: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [registeredSchool, setRegisteredSchool] = useState<School | null>(null);
  const [inviteStatus, setInviteStatus] = useState<'idle' | 'sending' | 'sent' | 'email_exists' | 'failed'>('idle');
  const [emailExistsPrompt, setEmailExistsPrompt] = useState(false);

  // Reset/populate form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        school_name: school?.school_name ?? '',
        address: school?.address ?? '',
        contact_person: school?.contact_person ?? '',
        email: school?.email ?? '',
        phone: school?.phone ?? '',
      });
      setValidationErrors({});
      setRegisteredSchool(null);
      setInviteStatus('idle');
      setEmailExistsPrompt(false);
    }
  }, [open, school]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateSchoolRequest) => createSchoolDirect(data),
    onSuccess: async (newSchool) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.all });
      setRegisteredSchool(newSchool);
      // Send admin invite
      setInviteStatus('sending');
      try {
        const result = await sendInviteDirect({
          email: newSchool.email,
          role: 'admin',
          schoolId: newSchool.school_id,
          schoolName: newSchool.school_name,
          name: newSchool.contact_person,
        });
        if (result.emailExists) {
          setInviteStatus('email_exists');
          setEmailExistsPrompt(true);
        } else {
          setInviteStatus('sent');
        }
      } catch {
        setInviteStatus('failed');
      }
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateSchoolRequest) => updateSchoolDirect(school!.school_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.schools.all });
      onClose();
    },
  });

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate school name
    if (!formData.school_name.trim()) {
      errors.school_name = 'School name is required';
    }

    // Validate address
    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    // Validate contact person
    if (!formData.contact_person.trim()) {
      errors.contact_person = 'Contact person is required';
    }

    // Validate email
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    // Validate phone
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^[\d\s\-\+\(\)]+$/.test(formData.phone)) {
      errors.phone = 'Invalid phone format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    if (isEditMode) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error || updateMutation.error;

  const handleClose = () => {
    setRegisteredSchool(null);
    setInviteStatus('idle');
    setEmailExistsPrompt(false);
    onClose();
  };

  const handleFieldChange = (field: keyof CreateSchoolRequest, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Show success message with School ID
  if (registeredSchool) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>School Registered Successfully!</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="success">The school has been registered successfully.</Alert>

            <Box sx={{ p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>School ID</Typography>
              <Typography variant="h5" fontFamily="monospace" fontWeight="bold" color="primary">
                {registeredSchool.school_id}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Share this ID with users who need to sign up for this school.
              </Typography>
            </Box>

            {/* Invite status */}
            {inviteStatus === 'sending' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Sending activation email to {registeredSchool.email}…</Typography>
              </Box>
            )}
            {inviteStatus === 'sent' && (
              <Alert severity="info">
                Activation email sent to <strong>{registeredSchool.email}</strong>. The admin can set their password using the link in the email.
              </Alert>
            )}
            {inviteStatus === 'email_exists' && (
              <Alert severity="warning">
                <strong>{registeredSchool.email}</strong> already has an account. Please ask them to log in — they can be assigned as admin from User Management.
              </Alert>
            )}
            {inviteStatus === 'failed' && (
              <Alert severity="warning">
                School registered, but the activation email could not be sent. You can resend it later from User Management.
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>School Details</Typography>
              <Typography variant="body2"><strong>Name:</strong> {registeredSchool.school_name}</Typography>
              <Typography variant="body2"><strong>Contact:</strong> {registeredSchool.contact_person}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {registeredSchool.email}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" disabled={inviteStatus === 'sending'}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Show registration form
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? 'Edit School' : 'Register New School'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {mutationError && (
            <Alert severity="error">
              {(mutationError as Error).message}
            </Alert>
          )}

          <TextField
            label="School Name"
            value={formData.school_name}
            onChange={(e) => handleFieldChange('school_name', e.target.value)}
            required
            fullWidth
            autoFocus
            error={!!validationErrors.school_name}
            helperText={validationErrors.school_name}
            placeholder="e.g., Sanskriti School"
          />

          <TextField
            label="Address"
            value={formData.address}
            onChange={(e) => handleFieldChange('address', e.target.value)}
            required
            fullWidth
            multiline
            rows={2}
            error={!!validationErrors.address}
            helperText={validationErrors.address}
            placeholder="Enter complete school address"
          />

          <TextField
            label="Contact Person"
            value={formData.contact_person}
            onChange={(e) => handleFieldChange('contact_person', e.target.value)}
            required
            fullWidth
            error={!!validationErrors.contact_person}
            helperText={validationErrors.contact_person}
            placeholder="Name of primary contact person"
          />

          <TextField
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => handleFieldChange('email', e.target.value)}
            required
            fullWidth
            error={!!validationErrors.email}
            helperText={validationErrors.email}
            placeholder="contact@school.edu"
          />

          <TextField
            label="Phone"
            value={formData.phone}
            onChange={(e) => handleFieldChange('phone', e.target.value)}
            required
            fullWidth
            error={!!validationErrors.phone}
            helperText={validationErrors.phone}
            placeholder="+91 1234567890"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isPending}
        >
          {isPending
            ? isEditMode ? 'Saving...' : 'Registering...'
            : isEditMode ? 'Save Changes' : 'Register School'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
